// ============================================
// StateSearchInput — Campo "Estado" con lupa: se escriben las primeras letras
// y se elige el estado de la lista (sin acentos ni mayúsculas).
// ============================================

import React, { useMemo, useRef, useState } from 'react';
import './StateSearchInput.css';

export const ESTADOS_MX = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
  'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango',
  'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco',
  'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla',
  'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora',
  'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas',
];

// Atajos comunes que la gente escribe.
const ALIASES = { cdmx: 'Ciudad de México', df: 'Ciudad de México', edomex: 'Estado de México' };

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="M20 20l-4.2-4.2" strokeLinecap="round" />
  </svg>
);

export default function StateSearchInput({ name = 'state', value, onChange, required }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    const q = norm(value);
    if (!q) return ESTADOS_MX;
    const alias = ALIASES[q.replace(/[\s.]/g, '')];
    const byWord = ESTADOS_MX.filter((e) => norm(e).split(' ').some((w) => w.startsWith(q)) || norm(e).startsWith(q));
    return alias && !byWord.includes(alias) ? [alias, ...byWord] : byWord;
  }, [value]);

  const emit = (v) => onChange({ target: { name, value: v } });

  const pick = (estado) => {
    emit(estado);
    setOpen(false);
  };

  // Al salir: si lo escrito corresponde a un solo estado, se completa.
  const onBlur = () => {
    setTimeout(() => setOpen(false), 120);
    const exact = ESTADOS_MX.find((e) => norm(e) === norm(value));
    if (exact) emit(exact);
    else if (matches.length === 1) emit(matches[0]);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && matches[active]) {
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const valid = ESTADOS_MX.includes(value);

  return (
    <div className="state-search">
      <input
        ref={inputRef}
        type="text"
        name={name}
        className="field-input state-search-input"
        placeholder="Busca tu estado"
        autoComplete="address-level1"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${name}-listbox`}
        aria-autocomplete="list"
        value={value}
        onChange={(e) => { emit(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        required={required}
        // Solo se acepta un estado de la lista.
        pattern={ESTADOS_MX.join('|')}
        title="Elige un estado de la lista"
      />
      <button
        type="button"
        className="state-search-icon"
        tabIndex={-1}
        aria-label="Buscar estado"
        onMouseDown={(e) => { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }}
      >
        <SearchIcon />
      </button>
      {open && !valid && matches.length > 0 && (
        <ul id={`${name}-listbox`} className="state-search-list" role="listbox">
          {matches.map((estado, i) => (
            <li
              key={estado}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'is-active' : ''}
              onMouseDown={(e) => { e.preventDefault(); pick(estado); }}
              onMouseEnter={() => setActive(i)}
            >
              {estado}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
